import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Keycloak from 'keycloak-js';
import ReactGA from 'react-ga4';

import { pathToUrl } from '../shared/utils/path-to-url/path-to-url';
import { HeaderRequestFields } from '../config';
import guid from '../shared/utils/guid';
import { logout } from '../store/authentication/actions';
import { RuntimeDuplicationError } from '../shared/notification/actions';
import { Store } from 'redux';
import { AppState } from '../store/appState';
import { log } from '../shared/utils/log';
import i18n, { parseLanguageCode } from '../../i18n';

const {
    HEADER_MOCKSERVER,
    HEADER_MOCKSERVER_TYPE,
    HEADER_BORROWBOX_REQUESTID,
    HEADER_BORROWBOX_REQUESTSCOPEID,
    HEADER_BORROWBOX_SPOC,
    HEADER_BORROWBOX_LANGUAGE
} = HeaderRequestFields;

type RequestTokenStore = { [key: string]: string };

export const http = axios.create();

const isTest = () => process.env.NODE_ENV === 'test';

export function initHttpClient(
    kc: Keycloak,
    store: Store<AppState>,
    additionalConfig: Partial<InternalAxiosRequestConfig> = {},
    enableRaceToken: boolean = true
) {
    const axiosRequestTokenStore: RequestTokenStore = {};

    initAxios(http, kc, store, axiosRequestTokenStore, additionalConfig, enableRaceToken);
}

function initAxios(
    it: AxiosInstance,
    kc: Keycloak,
    store: Store<AppState>,
    requestTokenStore: RequestTokenStore,
    additionalConfig: Partial<InternalAxiosRequestConfig> = {},
    enableRaceToken: boolean
) {
    if (additionalConfig.baseURL) {
        it.defaults.baseURL = additionalConfig.baseURL;
    }

    it.interceptors.request.use(requestHeadersInterceptor(kc, store));
    if (enableRaceToken) {
        it.interceptors.request.use(raceTokenRequestInterceptor(requestTokenStore));
    }

    it.interceptors.response.use(response => response, loggingErrorInterceptor);
    if (!isTest()) it.interceptors.response.use(timeLoggingResponseInterceptor);
    if (enableRaceToken) {
        it.interceptors.response.use(raceTokenResponseInterceptor(requestTokenStore));
    }
    it.interceptors.response.use(response => response, authenticationErrorInterceptor(kc));
}

function requestHeadersInterceptor(kc: Keycloak, store: Store<AppState>) {
    const currentLanguage = i18n.language;
    return (config: InternalAxiosRequestConfig) => {
        // Update type annotation
        config.headers.set({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...config.headers
        });

        if (!config.external) {
            // set auth header
            config.headers.Authorization = `Bearer ${kc.token}`;

            // spoc header
            const auth = store.getState().authentication;
            config.headers[HEADER_BORROWBOX_SPOC] = auth.spoc ? auth.spoc.id || '' : '';

            // language header
            if (!config.headers[HEADER_BORROWBOX_LANGUAGE]) {
                config.headers[HEADER_BORROWBOX_LANGUAGE] = parseLanguageCode(currentLanguage);
            }

            // set path params
            config.url = pathToUrl(config.url, config.pathParams || {});

            config.headers[HEADER_BORROWBOX_REQUESTSCOPEID] = auth.requestScopeId || '';
        }

        // measure the time the request takes to complete
        config.tsStart = new Date().getTime();

        // return modified config
        return config;
    };
}

function loggingErrorInterceptor(error: any): Promise<unknown> {
    log.axiosError(error);
    return Promise.reject(error);
}

function timeLoggingResponseInterceptor(response: AxiosResponse) {
    const tsNow = new Date().getTime();
    const tsStart = response.config.tsStart;
    if (tsStart) {
        const duration = tsNow - tsStart;
        ReactGA.event({
            category: response.config.url ?? 'unknown',
            action: 'load',
            value: duration,
            label: 'Backend Requests'
        });
    }
    return response;
}

function authenticationErrorInterceptor(kc: Keycloak) {
    return error => {
        if (error.status === 401 && kc.isTokenExpired()) {
            // eslint-disable-next-line no-restricted-globals
            if (confirm('Your session is no longer valid, please login again.')) {
                logout(kc);
            }
        }
        return Promise.reject(error);
    };
}

/**
 * Generates a token and adds it to the request. The token is expected to be sent back from the server.
 */
function raceTokenRequestInterceptor(axiosRequestTokenStore: RequestTokenStore) {
    return (config: InternalAxiosRequestConfig) => {
        if (!config.external) {
            // create a randomly chosen string as a token
            const token = guid();
            const key = getRequestKey(config);
            // store token for a request group, or the url
            axiosRequestTokenStore[key] = token;
            config.headers.set({
                ...config.headers,
                [HEADER_BORROWBOX_REQUESTID]: token
            });
        }
        return config;
    };
}

function getRequestKey(config: InternalAxiosRequestConfig) {
    return config.requestGroup || `${config.method} ${config.url}`;
}

/**
 * Checks whether the response contains a request token and compares it against the expected request token within
 * the given token store. If there is no matching expected token for the request URL or request group, the response
 * is rejected.
 *
 * This is required to avoid race conditions due to multiple requests to the same endpoint.
 */
function raceTokenResponseInterceptor(axiosRequestTokenStore: RequestTokenStore) {
    return (response: AxiosResponse) => {
        if (response.config.external) {
            return response;
        }

        const responseURL = response.request.responseURL ?? '';

        if (responseURL.includes('skipRuntimeDuplicationCheck')) {
            return response;
        }

        // needed because mock server can't mirror HEADER_BORROWBOX_REQUESTID header value
        if (response.headers[HEADER_MOCKSERVER] === HEADER_MOCKSERVER_TYPE) {
            return response;
        }

        const key = getRequestKey(response.config);
        const expectedToken = axiosRequestTokenStore[key];
        // if we have a stored token, it has to be the same as the one in the response
        const isTokenValid = !(expectedToken && expectedToken !== response.headers[HEADER_BORROWBOX_REQUESTID]);

        // if the token in the response is equal to that from the (last) request of the url,
        // then return the response, otherwise discard it.
        //TODO: find a way to cancel ongoing requests when loan format change is happening while init filter
        // return response;
        if (isTokenValid) {
            return response;
        }

        const customError = {
            message: 'race condition happened to the same borrowbox endpoint',
            runtimeDuplication: true,
            response,
            error: response //kept for backwards compatibility
        } as RuntimeDuplicationError;
        return Promise.reject(customError);
    };
}
